/**
 * Inventory: owned cosmetics, equip via drag-drop, unequip via card icon, outfits, Dots balance.
 * One card per cosmetic; Equip picks among purchased colors when applicable.
 * Cards are grouped by category (same order as Shop) with Shop-style blur hover.
 */
import {
  fetchShopCatalog,
  shopDeleteOutfit,
  shopEquipCosmetic,
  shopSaveOutfit,
  shopSetEquippedCosmetics,
  type CosmeticCategory,
  type CosmeticOutfitDto,
  type ShopCosmeticDto,
} from "../lib/cloud";
import {
  applyDotsFromApi,
  getDotsBalance,
  getDotsState,
  getEquippedThemeChannels,
  isCosmeticEquipped,
  setEquippedCosmeticIds,
  setEquippedThemeChannels,
  setOutfits,
} from "./dots-currency";
import {
  applyEquippedThemeCosmetics,
  registerThemeCosmeticBatch,
  themeCosmeticEquipSlots,
  THEME_COSMETIC_EQUIP_SLOT_LABELS,
  type ThemeCosmeticEquipSlot,
} from "./theme-cosmetic";
import { attachThemeCardChrome } from "./theme-cosmetic-preview";
import { refreshGameThemePresentation } from "./game-theme";
import {
  COSMETIC_ART_SIZE,
  COSMETIC_REF_CURSOR_R,
  cosmeticCardCursorRadius,
  drawCosmeticOverlay,
  drawCursorWithCosmeticAsync,
  getCachedCosmeticImage,
  loadCosmeticImage,
  preloadCosmeticImages,
} from "./cursor-cosmetics";
import { resolveCosmeticVisual } from "./cosmetic-recolor";
import { startCosmeticPreviewLoop, stopCosmeticPreviewLoop } from "./cosmetic-preview-loop";
import { ANIM_FRAME_MS } from "./shop-creator-fx";
import {
  BANNER_CARD_H,
  BANNER_CARD_W,
  ensureBannerCardImages,
  paintBannerCardArt,
} from "./banner-card-preview";
import {
  paintTrailCardIdle,
  paintTrailCardPreview,
  trailModeBadgeLabel,
  createTrailPreviewState,
  type TrailCosmeticVisual,
} from "./trail-cosmetics";
import {
  bindInventoryDragPointer,
  INVENTORY_GHOST_SIZE,
  startInventoryCosmeticDrag,
  unlockInventoryDragScroll,
} from "./inventory-cosmetic-drag";
import {
  buildAcquisitionLines,
  buildCrateSourceMap,
  filterIndexCatalog,
  filterIndexItems,
  groupIndexByCategory,
  INDEX_FILTER_OPTIONS,
  type IndexCrateSource,
  type IndexFilter,
} from "./inventory-index";

const CATEGORY_ORDER: CosmeticCategory[] = [
  "hat",
  "face",
  "cursor",
  "cursor_color",
  "trail",
  "banner",
  "theme",
];
const CATEGORY_LABELS: Record<CosmeticCategory, string> = {
  hat: "Hat",
  face: "Face",
  cursor: "Body",
  cursor_color: "Cursor",
  trail: "Trails",
  banner: "Banners",
  theme: "Themes",
};

const TRASH_ICON =
  '<svg class="inventoryOutfitTrashIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v9h-2V9zm4 0h2v9h-2V9zM7 9h2v9H7V9zm-1 12h12V8H6v13z"/></svg>';

/** Three vertical lines — drag handle for Equipped layer reorder. */
const EQUIPPED_GRIP_ICON =
  '<svg class="inventoryEquippedGripIcon" viewBox="0 0 12 16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M2 1h2v14H2zm4 0h2v14H6zm4 0h2v14h-2z"/></svg>';

/** Curved undo/revert arrow — unequip from card. */
const UNEQUIP_ICON =
  '<svg class="inventoryUnequipIcon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/></svg>';

/** Match Shop card zoom + blur crossfade sizing. */
const INV_CARD_FILL_RATIO = 1.22;
const INV_CARD_PREVIEW_SIZE = 220;
const OUTFIT_PREVIEW_SIZE = 160;

type InventoryMount = {
  screen: HTMLElement;
  balanceEl: HTMLElement;
  gridEl: HTMLElement;
  outfitsPanelEl: HTMLElement;
  indexPanelEl: HTMLElement;
  tabCosmeticsBtn: HTMLButtonElement;
  tabOutfitsBtn: HTMLButtonElement;
  tabIndexBtn: HTMLButtonElement;
  indexFilterEl: HTMLSelectElement;
  statusEl: HTMLElement;
  livePreviewCanvas: HTMLCanvasElement | null;
  bannerPreviewCanvas?: HTMLCanvasElement | null;
  equippedListEl: HTMLElement | null;
  isSignedIn: () => boolean;
  getCursorColor: () => string;
  getTrailColor: () => string;
  applyOutfitColors: (colors: { cursorColor?: string; trailColor?: string }) => void;
  onEquippedChanged: () => void;
};

let mount: InventoryMount | null = null;
let ownedItems: ShopCosmeticDto[] = [];
/** Full published catalog for Inventory → Index. */
let catalogItems: ShopCosmeticDto[] = [];
let crateSourceByCosmeticId = new Map<string, IndexCrateSource[]>();
let inventoryView: "cosmetics" | "outfits" | "index" = "cosmetics";
let indexFilter: IndexFilter = "all";
/** Resolved display URLs (with active purchase tint applied). */
const displayById = new Map<string, { imageData: string; animationFrames: string[] | null }>();
let livePreviewSerial = 0;
/** Cached resolved layers for the live preview RAF loop. */
let livePreviewLayers: Array<{
  imageData: string;
  animationFrames: string[] | null;
  category: CosmeticCategory;
  /** True when pixels remap to Settings cursor color (mask / legacy match). */
  matchCursor: boolean;
  newCursor: boolean;
}> = [];
/** Debounce match-cursor layer re-resolve while dragging RGB. */
let livePreviewResolveTimer: ReturnType<typeof setTimeout> | null = null;

function formatDots(n: number) {
  return `${Math.max(0, Math.floor(n)).toLocaleString()} Dots`;
}

function itemCategory(item: ShopCosmeticDto): CosmeticCategory {
  const c = item.category;
  if (
    c === "hat" ||
    c === "face" ||
    c === "cursor" ||
    c === "cursor_color" ||
    c === "trail" ||
    c === "banner" ||
    c === "theme"
  ) {
    return c;
  }
  return "cursor";
}

function ownedColorsOf(item: ShopCosmeticDto): string[] {
  const fromList = Array.isArray(item.ownedCustomColors) ? item.ownedCustomColors : [];
  const out: string[] = [];
  for (const c of fromList) {
    const s = String(c || "").trim().toLowerCase();
    if (/^#[0-9a-f]{6}$/.test(s) && !out.includes(s)) out.push(s);
  }
  if (!out.length && item.customColor) {
    const s = String(item.customColor).trim().toLowerCase();
    if (/^#[0-9a-f]{6}$/.test(s)) out.push(s);
  }
  return out;
}

function activeColorOf(item: ShopCosmeticDto): string | null {
  const active = String(item.activeCustomColor || item.customColor || "").trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(active)) return active;
  return ownedColorsOf(item)[0] ?? null;
}

async function resolveDisplay(item: ShopCosmeticDto, tint?: string | null): Promise<{
  imageData: string;
  animationFrames: string[] | null;
}> {
  const cursorHex = mount?.getCursorColor() || "#ffffff";
  const customTint = tint ?? activeColorOf(item);
  const cacheKey = `${item.id}:${cursorHex}:${item.matchCursorMaskData ? "mm" : ""}:${item.cursorColorShade ?? 0}:${customTint || "base"}`;
  const cached = displayById.get(cacheKey);
  if (cached) return cached;
  try {
    const resolved = await resolveCosmeticVisual(item, {
      cursorHex,
      customTint: item.allowCustomColor ? customTint : null,
    });
    displayById.set(cacheKey, resolved);
    return resolved;
  } catch {
    const frames =
      Array.isArray(item.animationFrames) && item.animationFrames.length
        ? item.animationFrames.filter((f) => typeof f === "string" && f.length > 0).slice(0, 10)
        : null;
    const resolved = { imageData: item.imageData, animationFrames: frames };
    displayById.set(cacheKey, resolved);
    return resolved;
  }
}

function paintCardLayer(
  canvas: HTMLCanvasElement,
  imageData: string,
  mode: "idle" | "preview",
  animationFrames?: string[] | null,
  opts?: { hideBaseCursor?: boolean },
) {
  const ctx = canvas.getContext("2d");
  if (!ctx || !mount) return;
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  const cx = w * 0.5;
  const cy = h * 0.5;
  const r = cosmeticCardCursorRadius(Math.min(w, h), INV_CARD_FILL_RATIO);
  const hideBase = !!opts?.hideBaseCursor;
  if (mode === "preview") {
    void drawCursorWithCosmeticAsync(ctx, cx, cy, r, mount.getCursorColor(), imageData, {
      cosmeticOnly: hideBase,
      animationFrames,
    });
  } else {
    void drawCursorWithCosmeticAsync(ctx, cx, cy, r, mount.getCursorColor(), imageData, {
      cosmeticOnly: true,
      // Idle: first animation frame only (hover loops all frames).
      animationFrames,
    });
  }
}

function itemMatchesCursor(item: ShopCosmeticDto): boolean {
  return !!(item.matchCursorColor || item.matchCursorMaskData || item.matchCursorMaskFrames?.length);
}

function ensureLivePreviewLoop() {
  if (!mount?.livePreviewCanvas) return;
  const canvas = mount.livePreviewCanvas;
  startCosmeticPreviewLoop(
    canvas,
    () => {
      drawLivePreviewFrame();
    },
    () => mount?.livePreviewCanvas === canvas && !mount.screen.classList.contains("hidden"),
  );
}

function drawLivePreviewFrame() {
  if (!mount?.livePreviewCanvas) return;
  const canvas = mount.livePreviewCanvas;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  const cx = w * 0.5;
  const cy = h * 0.5;
  const fill = Math.min(w, h) * 0.78;
  const r = fill / (COSMETIC_ART_SIZE / COSMETIC_REF_CURSOR_R);
  const cursorColor = mount.getCursorColor();

  // Settings RGB body first — always readable while tuning color (unless New Cursor).
  if (!livePreviewLayers.some((l) => l.newCursor)) {
    ctx.save();
    ctx.fillStyle = cursorColor;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  for (const layer of livePreviewLayers) {
    drawCosmeticOverlay(ctx, cx, cy, r, getCachedCosmeticImage(layer.imageData));
    if (layer.animationFrames?.length) {
      const fi = Math.floor(performance.now() / ANIM_FRAME_MS) % layer.animationFrames.length;
      drawCosmeticOverlay(ctx, cx, cy, r, getCachedCosmeticImage(layer.animationFrames[fi]));
    }
  }
}

async function paintLivePreview() {
  if (!mount?.livePreviewCanvas) return;
  const canvas = mount.livePreviewCanvas;
  const serial = ++livePreviewSerial;
  const byId = new Map(ownedItems.map((item) => [item.id, item]));
  const layers: Array<{
    imageData: string;
    animationFrames: string[] | null;
    category: CosmeticCategory;
    matchCursor: boolean;
    newCursor: boolean;
  }> = [];
  for (const id of getDotsState().equippedCosmeticIds) {
    if (serial !== livePreviewSerial) return;
    const item = byId.get(id);
    if (!item) continue;
    // Trails/banners are not drawn on the play cursor — skip in cursor live preview.
    if (itemCategory(item) === "trail" || itemCategory(item) === "banner" || itemCategory(item) === "theme") continue;
    const layer = await resolveDisplay(item);
    if (serial !== livePreviewSerial) return;
    await loadCosmeticImage(layer.imageData);
    if (layer.animationFrames?.length) {
      await Promise.all(layer.animationFrames.map((f) => loadCosmeticImage(f)));
    }
    if (serial !== livePreviewSerial) return;
    layers.push({
      ...layer,
      category: itemCategory(item),
      matchCursor: itemMatchesCursor(item),
      newCursor: !!item.newCursor,
    });
  }
  if (serial !== livePreviewSerial) return;
  livePreviewLayers = layers;
  drawLivePreviewFrame();
  ensureLivePreviewLoop();
  void paintBannerPreview(serial);
}

async function paintBannerPreview(serial: number) {
  const canvas = mount?.bannerPreviewCanvas;
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  stopCosmeticPreviewLoop(canvas);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "rgba(20, 28, 48, 0.85)";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const byId = new Map(ownedItems.map((item) => [item.id, item]));
  let banner: ShopCosmeticDto | null = null;
  for (const id of getDotsState().equippedCosmeticIds) {
    const item = byId.get(id);
    if (item && itemCategory(item) === "banner") {
      banner = item;
      break;
    }
  }
  if (!banner) {
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    ctx.font = '9px "Press Start 2P", monospace';
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("No banner", canvas.width / 2, canvas.height / 2);
    return;
  }
  const layer = await resolveDisplay(banner);
  if (serial !== livePreviewSerial || !mount) return;
  await loadCosmeticImage(layer.imageData);
  if (layer.animationFrames?.length) {
    await Promise.all(layer.animationFrames.map((f) => loadCosmeticImage(f)));
  }
  if (serial !== livePreviewSerial) return;

  const drawFrame = () => {
    if (!mount?.bannerPreviewCanvas || serial !== livePreviewSerial) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "rgba(20, 28, 48, 0.85)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    let src = getCachedCosmeticImage(layer.imageData);
    if (layer.animationFrames?.length) {
      const fi = Math.floor(performance.now() / ANIM_FRAME_MS) % layer.animationFrames.length;
      src = getCachedCosmeticImage(layer.animationFrames[fi]) || src;
    }
    if (!src) return;
    const iw = src.naturalWidth || src.width || 256;
    const ih = src.naturalHeight || src.height || 64;
    const scale = Math.min(canvas.width / iw, canvas.height / ih);
    const dw = iw * scale;
    const dh = ih * scale;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(src, (canvas.width - dw) / 2, (canvas.height - dh) / 2, dw, dh);
  };
  drawFrame();
  if (layer.animationFrames?.length) {
    startCosmeticPreviewLoop(canvas, drawFrame, () => !!mount && serial === livePreviewSerial);
  }
}

function openEquipColorModal(
  item: ShopCosmeticDto,
  colors: string[],
  current: string | null,
): Promise<string | "cancel"> {
  return new Promise((resolve) => {
    const modal = document.createElement("div");
    modal.className = "adminShopPublishModal screen";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    const box = document.createElement("div");
    box.className = "modeDetailModalPanel modeBox";
    box.innerHTML = `<h2>Equip ${item.name}</h2>
      <p class="accessHint">Choose one of your purchased colors.</p>`;

    const preview = document.createElement("canvas");
    preview.width = INV_CARD_PREVIEW_SIZE;
    preview.height = INV_CARD_PREVIEW_SIZE;
    preview.className = "shopCosmeticCardPreview";

    let selected = current && colors.includes(current) ? current : colors[0];

    const refreshPreview = () => {
      void resolveDisplay(item, selected).then(async (layer) => {
        if (layer.animationFrames?.length) {
          await Promise.all(layer.animationFrames.map((f) => loadCosmeticImage(f)));
        }
        paintCardLayer(preview, layer.imageData, "preview", layer.animationFrames, {
          hideBaseCursor: !!item.newCursor,
        });
        if (layer.animationFrames?.length) {
          startCosmeticPreviewLoop(
            preview,
            () =>
              paintCardLayer(preview, layer.imageData, "preview", layer.animationFrames, {
                hideBaseCursor: !!item.newCursor,
              }),
            () => preview.isConnected,
          );
        } else {
          stopCosmeticPreviewLoop(preview);
        }
      });
    };

    const swatches = document.createElement("div");
    swatches.className = "inventoryColorSwatches";
    for (const color of colors) {
      const sw = document.createElement("button");
      sw.type = "button";
      sw.className = "inventoryColorSwatch";
      sw.style.background = color;
      sw.title = color;
      sw.setAttribute("aria-label", `Color ${color}`);
      if (color === selected) sw.classList.add("inventoryColorSwatch--active");
      sw.onclick = () => {
        selected = color;
        swatches.querySelectorAll(".inventoryColorSwatch").forEach((el) => {
          el.classList.toggle("inventoryColorSwatch--active", el === sw);
        });
        refreshPreview();
      };
      swatches.appendChild(sw);
    }

    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn";
    cancelBtn.textContent = "Cancel";
    const okBtn = document.createElement("button");
    okBtn.type = "button";
    okBtn.className = "btn green";
    okBtn.textContent = "Equip";

    box.appendChild(preview);
    box.appendChild(swatches);
    const actions = document.createElement("div");
    actions.className = "rgbRow";
    actions.style.justifyContent = "flex-end";
    actions.style.gap = "8px";
    actions.appendChild(cancelBtn);
    actions.appendChild(okBtn);
    box.appendChild(actions);
    modal.appendChild(box);
    document.body.appendChild(modal);
    refreshPreview();

    const close = (v: string | "cancel") => {
      stopCosmeticPreviewLoop(preview);
      modal.remove();
      resolve(v);
    };
    cancelBtn.onclick = () => close("cancel");
    okBtn.onclick = () => close(selected);
  });
}

function localPlaceEquipped(id: string, category: CosmeticCategory) {
  const byId = new Map(ownedItems.map((i) => [i.id, i]));
  const categoryOf = (eid: string) => {
    const item = byId.get(eid);
    return item ? itemCategory(item) : null;
  };
  // One Cursor (always bottom layer) + one Trail + one Banner; other items stack on top.
  let cur = getDotsState().equippedCosmeticIds.filter((x) => x !== id);
  if (category === "cursor_color" || category === "trail" || category === "banner") {
    cur = cur.filter((eid) => categoryOf(eid) !== category);
  }
  let next = category === "cursor_color" ? [id, ...cur] : [...cur, id];
  const bottoms: string[] = [];
  const tops: string[] = [];
  for (const eid of next) {
    if (categoryOf(eid) === "cursor_color") bottoms.push(eid);
    else tops.push(eid);
  }
  next = [...bottoms, ...tops];
  let colorKeep: string | null = category === "cursor_color" ? id : null;
  let trailKeep: string | null = category === "trail" ? id : null;
  let bannerKeep: string | null = category === "banner" ? id : null;
  const out: string[] = [];
  for (const eid of next) {
    const cat = categoryOf(eid);
    if (cat === "cursor_color") {
      if (colorKeep == null) colorKeep = eid;
      if (eid === colorKeep) out.push(eid);
      continue;
    }
    if (cat === "trail") {
      if (trailKeep == null) trailKeep = eid;
      if (eid === trailKeep) out.push(eid);
      continue;
    }
    if (cat === "banner") {
      if (bannerKeep == null) bannerKeep = eid;
      if (eid === bannerKeep) out.push(eid);
      continue;
    }
    out.push(eid);
  }
  return out;
}

function openThemeEquipModal(
  item: ShopCosmeticDto,
): Promise<ThemeCosmeticEquipSlot[] | "cancel"> {
  const available = themeCosmeticEquipSlots(item.themeData);
  if (!available.length) return Promise.resolve("cancel");

  const current = getEquippedThemeChannels();
  const alreadyOn = available.some((k) => current[k] === item.id);

  return new Promise((resolve) => {
    const modal = document.createElement("div");
    modal.className = "inventoryThemeEquipModal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.setAttribute("aria-label", alreadyOn ? `Change ${item.name}` : `Equip ${item.name}`);

    const box = document.createElement("div");
    box.className = "inventoryThemeEquipPanel modeBox";

    const title = document.createElement("h2");
    title.textContent = item.name;
    box.appendChild(title);

    const selected = new Set<ThemeCosmeticEquipSlot>(
      available.filter((k) => current[k] === item.id),
    );
    // If nothing from this theme is equipped yet, pre-check all available.
    if (!alreadyOn) {
      available.forEach((k) => selected.add(k));
    }

    const hint = document.createElement("p");
    hint.className = "accessHint";
    hint.textContent = alreadyOn
      ? "Uncheck a channel to unequip it. Leave none checked to unequip this Theme."
      : "Choose which channels to equip from this Theme.";
    box.appendChild(hint);

    const list = document.createElement("div");
    list.className = "inventoryThemeEquipList";
    for (const key of available) {
      const label = document.createElement("label");
      label.className = "inventoryThemeEquipOption";
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.checked = selected.has(key);
      cb.onchange = () => {
        if (cb.checked) selected.add(key);
        else selected.delete(key);
      };
      const span = document.createElement("span");
      span.textContent = THEME_COSMETIC_EQUIP_SLOT_LABELS[key];
      label.appendChild(cb);
      label.appendChild(span);
      list.appendChild(label);
    }
    box.appendChild(list);

    const actions = document.createElement("div");
    actions.className = "inventoryThemeEquipActions";
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn";
    cancelBtn.textContent = "Cancel";
    const doneBtn = document.createElement("button");
    doneBtn.type = "button";
    doneBtn.className = "btn green";
    doneBtn.textContent = "Done";
    actions.appendChild(cancelBtn);
    actions.appendChild(doneBtn);
    box.appendChild(actions);
    modal.appendChild(box);
    document.body.appendChild(modal);

    const close = (v: ThemeCosmeticEquipSlot[] | "cancel") => {
      modal.remove();
      resolve(v);
    };
    cancelBtn.onclick = () => close("cancel");
    doneBtn.onclick = () => {
      // Empty selection = unequip all slots of this Theme.
      const picked = available.filter((k) => selected.has(k));
      close(picked);
    };
    modal.addEventListener("click", (ev) => {
      if (ev.target === modal) close("cancel");
    });
  });
}

async function equipItem(item: ShopCosmeticDto): Promise<boolean> {
  if (!mount) return false;
  if (!mount.isSignedIn()) {
    mount.statusEl.textContent = "Sign in to equip cosmetics.";
    return false;
  }

  if (itemCategory(item) === "theme") {
    const picked = await openThemeEquipModal(item);
    if (picked === "cancel") return false;
    try {
      const res = await shopEquipCosmetic(item.id, "equip", { themeChannels: picked });
      if (res.dots) applyDotsFromApi(res.dots);
      else {
        if (res.equippedCosmeticIds) setEquippedCosmeticIds(res.equippedCosmeticIds);
        if (res.equippedThemeChannels) setEquippedThemeChannels(res.equippedThemeChannels);
      }
      applyEquippedThemeCosmetics();
      refreshGameThemePresentation();
      mount.onEquippedChanged();
      renderActiveView();
      if (!picked.length) {
        mount.statusEl.textContent = `Unequipped ${item.name}.`;
      } else {
        mount.statusEl.textContent = `Equipped ${item.name} (${picked.length} channel${picked.length === 1 ? "" : "s"}).`;
      }
      return true;
    } catch (e) {
      mount.statusEl.textContent = e instanceof Error ? e.message : "Equip failed.";
      return false;
    }
  }

  if (isCosmeticEquipped(item.id)) {
    mount.statusEl.textContent = `${item.name} is already equipped.`;
    return true;
  }

  try {
    const colors = item.allowCustomColor ? ownedColorsOf(item) : [];
    let customColor: string | null | undefined;
    if (colors.length > 1) {
      const picked = await openEquipColorModal(item, colors, activeColorOf(item));
      if (picked === "cancel") return false;
      customColor = picked;
    } else if (colors.length === 1) {
      customColor = colors[0];
    }

    const res = await shopEquipCosmetic(item.id, "equip", { customColor });
    if (res.dots) applyDotsFromApi(res.dots);
    else if (res.equippedCosmeticIds) setEquippedCosmeticIds(res.equippedCosmeticIds);
    else {
      setEquippedCosmeticIds(localPlaceEquipped(item.id, itemCategory(item)));
    }
    const idx = ownedItems.findIndex((i) => i.id === item.id);
    if (idx >= 0) {
      ownedItems[idx] = {
        ...ownedItems[idx],
        ownedCustomColors: res.ownedCustomColors ?? ownedItems[idx].ownedCustomColors,
        activeCustomColor: res.activeCustomColor ?? customColor ?? ownedItems[idx].activeCustomColor,
      };
    }
    mount.onEquippedChanged();
    renderActiveView();
    mount.statusEl.textContent = `Equipped ${item.name}.`;
    return true;
  } catch (e) {
    mount.statusEl.textContent = e instanceof Error ? e.message : "Equip failed.";
    return false;
  }
}

function inventoryDropZones(): HTMLElement[] {
  const zones: HTMLElement[] = [];
  const cursor = document.getElementById("inventoryCursorDropZone");
  const banner = document.getElementById("inventoryBannerDropZone");
  if (cursor instanceof HTMLElement) zones.push(cursor);
  if (banner instanceof HTMLElement) zones.push(banner);
  return zones;
}

/** Zones where dropping during an unequip drag keeps the item equipped. */
function inventoryUnequipKeepZones(): HTMLElement[] {
  const zones = inventoryDropZones();
  if (mount?.equippedListEl) zones.push(mount.equippedListEl);
  return zones;
}

function topEquippedCursorItem(): ShopCosmeticDto | null {
  const byId = new Map(ownedItems.map((item) => [item.id, item]));
  const ids = getDotsState().equippedCosmeticIds;
  for (let i = ids.length - 1; i >= 0; i--) {
    const item = byId.get(ids[i]);
    if (!item) continue;
    const cat = itemCategory(item);
    if (cat === "trail" || cat === "banner") continue;
    return item;
  }
  return null;
}

function equippedBannerItem(): ShopCosmeticDto | null {
  const byId = new Map(ownedItems.map((item) => [item.id, item]));
  for (const id of getDotsState().equippedCosmeticIds) {
    const item = byId.get(id);
    if (item && itemCategory(item) === "banner") return item;
  }
  return null;
}

async function makeItemGhostCanvas(
  item: ShopCosmeticDto,
  size = INVENTORY_GHOST_SIZE,
): Promise<HTMLCanvasElement> {
  const canvas = document.createElement("canvas");
  const isBanner = itemCategory(item) === "banner";
  canvas.width = isBanner ? BANNER_CARD_W : size;
  canvas.height = isBanner ? BANNER_CARD_H : size;
  const cat = itemCategory(item);
  if (cat === "trail") {
    const trailVis: TrailCosmeticVisual = {
      trailMode: item.trailMode === "particle" ? "particle" : "multiColor",
      trailColors: item.trailColors ?? null,
      imageData: item.imageData,
      animationFrames: item.animationFrames ?? null,
    };
    await paintTrailCardIdle(canvas, trailVis);
    return canvas;
  }
  const layer = await resolveDisplay(item);
  if (isBanner) {
    await ensureBannerCardImages(layer.imageData, layer.animationFrames);
    paintBannerCardArt(canvas, layer.imageData, layer.animationFrames, "idle");
    return canvas;
  }
  await loadCosmeticImage(layer.imageData);
  if (layer.animationFrames?.length) {
    await Promise.all(layer.animationFrames.map((f) => loadCosmeticImage(f)));
  }
  const idleFrames = layer.animationFrames?.length ? [layer.animationFrames[0]] : null;
  paintCardLayer(canvas, layer.imageData, "idle", idleFrames, {
    hideBaseCursor: !!item.newCursor,
  });
  return canvas;
}

function ghostDisplayForItem(item: ShopCosmeticDto): { width: number; height: number } {
  if (itemCategory(item) === "banner") {
    const width = Math.min(280, BANNER_CARD_W);
    return { width, height: width * (BANNER_CARD_H / BANNER_CARD_W) };
  }
  return { width: INVENTORY_GHOST_SIZE, height: INVENTORY_GHOST_SIZE };
}

function bindCardEquipDrag(
  _card: HTMLElement,
  previewWrap: HTMLElement,
  idleCanvas: HTMLCanvasElement,
  item: ShopCosmeticDto,
) {
  if (isCosmeticEquipped(item.id)) return;

  previewWrap.classList.add("inventoryCosmeticPreview--draggable");
  previewWrap.title = "Drag onto Your cursor (or Banner) to equip";

  let dragging = false;
  bindInventoryDragPointer(
    previewWrap,
    (e) => {
      if (dragging) {
        unlockInventoryDragScroll();
        return;
      }
      dragging = true;
      const pointerId = e.pointerId;
      void startInventoryCosmeticDrag(pointerId, e, {
        sourcePreview: previewWrap,
        sourceVisual: idleCanvas,
        ghostDisplaySize: ghostDisplayForItem(item),
        mode: {
          kind: "equip",
          category: itemCategory(item),
          dropZones: inventoryDropZones(),
          onDropEquip: () => equipItem(item),
        },
      }).finally(() => {
        dragging = false;
      });
    },
    {
      isBlocked: (ev) => !!(ev.target as HTMLElement | null)?.closest?.(".inventoryUnequipBtn"),
    },
  );
}

function bindUnequipDragFromElement(
  handleEl: HTMLElement,
  sourcePreview: HTMLElement,
  getItem: () => ShopCosmeticDto | null,
  getVisual: () => Promise<HTMLCanvasElement | HTMLElement> | HTMLCanvasElement | HTMLElement,
): () => void {
  handleEl.classList.add("inventoryCosmeticPreview--draggable");
  let dragging = false;
  return bindInventoryDragPointer(
    handleEl,
    (e) => {
      if (dragging) {
        unlockInventoryDragScroll();
        return;
      }
      const item = getItem();
      if (!item) {
        unlockInventoryDragScroll();
        return;
      }
      dragging = true;
      const pointerId = e.pointerId;
      let lastE = e;
      let aborted = false;
      const track = (ev: PointerEvent) => {
        if (ev.pointerId === pointerId) {
          ev.preventDefault();
          lastE = ev;
        }
      };
      const cancelPrep = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        aborted = true;
        window.removeEventListener("pointermove", track);
        window.removeEventListener("pointerup", cancelPrep);
        window.removeEventListener("pointercancel", cancelPrep);
        dragging = false;
        unlockInventoryDragScroll();
      };
      window.addEventListener("pointermove", track, { passive: false });
      window.addEventListener("pointerup", cancelPrep);
      window.addEventListener("pointercancel", cancelPrep);

      void (async () => {
        try {
          const visual = await Promise.resolve(getVisual());
          if (aborted) return;
          window.removeEventListener("pointermove", track);
          window.removeEventListener("pointerup", cancelPrep);
          window.removeEventListener("pointercancel", cancelPrep);
          await startInventoryCosmeticDrag(pointerId, lastE, {
            sourcePreview,
            sourceVisual: visual,
            ghostDisplaySize: ghostDisplayForItem(item),
            mode: {
              kind: "unequip",
              keepZones: inventoryUnequipKeepZones(),
              onUnequip: () => unequipItem(item),
            },
          });
        } catch {
          if (!aborted) unlockInventoryDragScroll();
        } finally {
          dragging = false;
        }
      })();
    },
    {
      isBlocked: (ev) =>
        !!(ev.target as HTMLElement | null)?.closest?.(
          ".inventoryUnequipBtn, .inventoryEquippedGrip, .inventoryEquippedUnequipBtn",
        ),
    },
  );
}

let previewUnequipBound = false;
let previewUnequipUnbinds: Array<() => void> = [];

function bindPreviewUnequipDrags() {
  if (!mount || previewUnequipBound) return;
  previewUnequipBound = true;
  const cursorZone = document.getElementById("inventoryCursorDropZone");
  const bannerZone = document.getElementById("inventoryBannerDropZone");
  const cursorCanvas = mount.livePreviewCanvas;
  const bannerCanvas = mount.bannerPreviewCanvas ?? null;

  if (cursorZone instanceof HTMLElement && cursorCanvas) {
    cursorZone.title = "Drag the top equipped item out to unequip";
    previewUnequipUnbinds.push(
      bindUnequipDragFromElement(
        cursorZone,
        cursorCanvas,
        () => topEquippedCursorItem(),
        async () => {
          const item = topEquippedCursorItem();
          return item ? makeItemGhostCanvas(item) : cursorCanvas;
        },
      ),
    );
  }
  if (bannerZone instanceof HTMLElement && bannerCanvas) {
    bannerZone.title = "Drag the banner out to unequip";
    previewUnequipUnbinds.push(
      bindUnequipDragFromElement(
        bannerZone,
        bannerCanvas,
        () => equippedBannerItem(),
        async () => {
          const item = equippedBannerItem();
          return item ? makeItemGhostCanvas(item) : bannerCanvas;
        },
      ),
    );
  }
}

function createCard(item: ShopCosmeticDto, equipped: Set<string>): HTMLElement {
  const card = document.createElement("article");
  card.className = "inventoryCosmeticCard";
  if (equipped.has(item.id)) card.classList.add("inventoryCosmeticCard--equipped");

  const isTrail = itemCategory(item) === "trail";
  const isBanner = itemCategory(item) === "banner";
  const isTheme = itemCategory(item) === "theme";
  const trailVis: TrailCosmeticVisual | null = isTrail
    ? {
        trailMode: item.trailMode === "particle" ? "particle" : "multiColor",
        trailColors: item.trailColors ?? null,
        imageData: item.imageData,
        animationFrames: item.animationFrames ?? null,
      }
    : null;
  if (trailVis) {
    const badge = document.createElement("span");
    badge.className = "shopCosmeticTrailBadge";
    badge.textContent = trailModeBadgeLabel(trailVis.trailMode);
    card.appendChild(badge);
  }
  if (isTheme) {
    // Label + eye attached after previewWrap exists (below).
  }

  if (equipped.has(item.id)) {
    const unequipBtn = document.createElement("button");
    unequipBtn.type = "button";
    unequipBtn.className = "inventoryUnequipBtn";
    unequipBtn.setAttribute("aria-label", `Unequip ${item.name}`);
    unequipBtn.title = "Unequip";
    unequipBtn.innerHTML = UNEQUIP_ICON;
    unequipBtn.disabled = !mount?.isSignedIn();
    unequipBtn.onclick = (ev) => {
      ev.stopPropagation();
      void unequipItem(item, unequipBtn);
    };
    card.appendChild(unequipBtn);
  }

  const previewWrap = document.createElement("div");
  previewWrap.className = isBanner
    ? "shopCosmeticCardPreviewWrap shopCosmeticCardPreviewWrap--banner"
    : "shopCosmeticCardPreviewWrap";

  const idleCanvas = document.createElement("canvas");
  idleCanvas.className = isBanner
    ? "shopCosmeticCardPreview shopCosmeticCardPreview--idle inventoryCosmeticCardPreview shopCosmeticCardPreview--banner"
    : "shopCosmeticCardPreview shopCosmeticCardPreview--idle inventoryCosmeticCardPreview";
  idleCanvas.width = isBanner ? BANNER_CARD_W : INV_CARD_PREVIEW_SIZE;
  idleCanvas.height = isBanner ? BANNER_CARD_H : INV_CARD_PREVIEW_SIZE;

  const hoverCanvas = document.createElement("canvas");
  hoverCanvas.className = isBanner
    ? "shopCosmeticCardPreview shopCosmeticCardPreview--hover inventoryCosmeticCardPreview shopCosmeticCardPreview--banner"
    : "shopCosmeticCardPreview shopCosmeticCardPreview--hover inventoryCosmeticCardPreview";
  hoverCanvas.width = isBanner ? BANNER_CARD_W : INV_CARD_PREVIEW_SIZE;
  hoverCanvas.height = isBanner ? BANNER_CARD_H : INV_CARD_PREVIEW_SIZE;
  hoverCanvas.setAttribute("aria-hidden", "true");

  previewWrap.appendChild(idleCanvas);
  previewWrap.appendChild(hoverCanvas);
  card.appendChild(previewWrap);

  if (isTheme) {
    attachThemeCardChrome(card, previewWrap, item.themeData, { idleCanvas, hoverCanvas });
  }

  const name = document.createElement("h3");
  name.className = "inventoryCosmeticCardName";
  name.textContent = item.name;
  card.appendChild(name);

  const colors = ownedColorsOf(item);
  if (colors.length > 0) {
    const swatches = document.createElement("div");
    swatches.className = "inventoryColorSwatches inventoryColorSwatches--card";
    for (const color of colors) {
      const sw = document.createElement("span");
      sw.className = "inventoryColorSwatch";
      sw.style.background = color;
      if (color === activeColorOf(item) && equipped.has(item.id)) {
        sw.classList.add("inventoryColorSwatch--active");
      }
      swatches.appendChild(sw);
    }
    card.appendChild(swatches);
  }

  bindCardEquipDrag(card, previewWrap, idleCanvas, item);

  if (isTheme) {
    const equipBtn = document.createElement("button");
    equipBtn.type = "button";
    equipBtn.className = "btn inventoryThemeEquipBtn";
    const alreadyOn = isCosmeticEquipped(item.id);
    equipBtn.textContent = alreadyOn ? "Change" : "Equip";
    equipBtn.disabled = !mount?.isSignedIn();
    equipBtn.onclick = (ev) => {
      ev.stopPropagation();
      void equipItem(item);
    };
    card.appendChild(equipBtn);
  }

  if (trailVis) {
    void paintTrailCardIdle(idleCanvas, trailVis);
    let previewState = createTrailPreviewState();
    let t0 = performance.now();
    const paintHover = async () => {
      const tSec = (performance.now() - t0) / 1000;
      previewState = await paintTrailCardPreview(hoverCanvas, trailVis, tSec, "#ffffff", previewState);
    };
    void paintHover();
    const startHoverAnim = () => {
      previewState = createTrailPreviewState();
      t0 = performance.now();
      startCosmeticPreviewLoop(
        hoverCanvas,
        () => paintHover(),
        () =>
          card.isConnected &&
          (card.matches(":hover") || card.classList.contains("inventoryCosmeticCard--preview")),
      );
    };
    const stopHoverAnim = () => stopCosmeticPreviewLoop(hoverCanvas);
    card.addEventListener("mouseenter", startHoverAnim);
    card.addEventListener("mouseleave", stopHoverAnim);
    previewWrap.addEventListener(
      "touchstart",
      () => {
        card.classList.add("inventoryCosmeticCard--preview");
        startHoverAnim();
      },
      { passive: true },
    );
    previewWrap.addEventListener(
      "touchend",
      () => {
        card.classList.remove("inventoryCosmeticCard--preview");
        stopHoverAnim();
      },
      { passive: true },
    );
    previewWrap.addEventListener(
      "touchcancel",
      () => {
        card.classList.remove("inventoryCosmeticCard--preview");
        stopHoverAnim();
      },
      { passive: true },
    );
    return card;
  }

  if (isBanner) {
    void resolveDisplay(item).then(async (layer) => {
      await ensureBannerCardImages(layer.imageData, layer.animationFrames);
      paintBannerCardArt(idleCanvas, layer.imageData, layer.animationFrames, "idle");
      paintBannerCardArt(hoverCanvas, layer.imageData, layer.animationFrames, "preview");
      const hasAnim = !!(layer.animationFrames && layer.animationFrames.length > 0);
      const startHoverAnim = () => {
        if (!hasAnim) return;
        startCosmeticPreviewLoop(
          hoverCanvas,
          () => paintBannerCardArt(hoverCanvas, layer.imageData, layer.animationFrames, "preview"),
          () =>
            card.isConnected &&
            (card.matches(":hover") || card.classList.contains("inventoryCosmeticCard--preview")),
        );
      };
      const stopHoverAnim = () => stopCosmeticPreviewLoop(hoverCanvas);
      card.addEventListener("mouseenter", startHoverAnim);
      card.addEventListener("mouseleave", stopHoverAnim);
      previewWrap.addEventListener(
        "touchstart",
        () => {
          card.classList.add("inventoryCosmeticCard--preview");
          startHoverAnim();
        },
        { passive: true },
      );
      previewWrap.addEventListener(
        "touchend",
        () => {
          card.classList.remove("inventoryCosmeticCard--preview");
          stopHoverAnim();
        },
        { passive: true },
      );
      previewWrap.addEventListener(
        "touchcancel",
        () => {
          card.classList.remove("inventoryCosmeticCard--preview");
          stopHoverAnim();
        },
        { passive: true },
      );
    });
    return card;
  }

  if (isTheme) {
    return card;
  }

  void resolveDisplay(item).then(async (layer) => {
    await loadCosmeticImage(layer.imageData);
    if (layer.animationFrames?.length) {
      await Promise.all(layer.animationFrames.map((f) => loadCosmeticImage(f)));
    }
    const idleFrames =
      layer.animationFrames?.length ? [layer.animationFrames[0]] : null;
    const hideBase = !!item.newCursor;
    paintCardLayer(idleCanvas, layer.imageData, "idle", idleFrames, { hideBaseCursor: hideBase });
    paintCardLayer(hoverCanvas, layer.imageData, "preview", layer.animationFrames, {
      hideBaseCursor: hideBase,
    });

    const hasAnim = !!(layer.animationFrames && layer.animationFrames.length > 0);
    const startHoverAnim = () => {
      if (!hasAnim) return;
      startCosmeticPreviewLoop(
        hoverCanvas,
        () => {
          paintCardLayer(hoverCanvas, layer.imageData, "preview", layer.animationFrames, {
            hideBaseCursor: hideBase,
          });
        },
        () =>
          card.isConnected &&
          (card.matches(":hover") || card.classList.contains("inventoryCosmeticCard--preview")),
      );
    };
    const stopHoverAnim = () => stopCosmeticPreviewLoop(hoverCanvas);

    card.addEventListener("mouseenter", startHoverAnim);
    card.addEventListener("mouseleave", stopHoverAnim);
    previewWrap.addEventListener(
      "touchstart",
      () => {
        card.classList.add("inventoryCosmeticCard--preview");
        startHoverAnim();
      },
      { passive: true },
    );
    previewWrap.addEventListener(
      "touchend",
      () => {
        card.classList.remove("inventoryCosmeticCard--preview");
        stopHoverAnim();
      },
      { passive: true },
    );
    previewWrap.addEventListener(
      "touchcancel",
      () => {
        card.classList.remove("inventoryCosmeticCard--preview");
        stopHoverAnim();
      },
      { passive: true },
    );
  });

  return card;
}

function toBlackSilhouette(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.save();
  ctx.globalCompositeOperation = "source-in";
  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.restore();
}

async function paintIndexSilhouette(
  canvas: HTMLCanvasElement,
  item: ShopCosmeticDto,
  layer: { imageData: string; animationFrames: string[] | null },
) {
  const cat = itemCategory(item);
  if (cat === "trail") {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    if (item.trailMode === "particle") {
      // One centered stamp — full idle path looks like repeated copies when silhouetted.
      const sole = layer.animationFrames?.[0] || layer.imageData;
      const img = await loadCosmeticImage(sole);
      if (img && img.naturalWidth > 0) {
        const size = Math.max(40, Math.min(w, h) * 0.48);
        ctx.drawImage(img, (w - size) * 0.5, (h - size) * 0.5, size, size);
      }
    } else {
      const trailVis: TrailCosmeticVisual = {
        trailMode: "multiColor",
        trailColors: item.trailColors ?? null,
        imageData: layer.imageData,
        animationFrames: layer.animationFrames,
      };
      await paintTrailCardIdle(canvas, trailVis);
    }
    toBlackSilhouette(canvas);
    return;
  }
  if (cat === "banner") {
    await ensureBannerCardImages(layer.imageData, layer.animationFrames);
    const soleImage = layer.animationFrames?.[0] || layer.imageData;
    paintBannerCardArt(canvas, soleImage, null, "idle");
    toBlackSilhouette(canvas);
    return;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx || !mount) return;
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  const cx = w * 0.5;
  const cy = h * 0.5;
  const r = cosmeticCardCursorRadius(Math.min(w, h), INV_CARD_FILL_RATIO);
  // Single layer only — stacking imageData + frame doubles the black silhouette.
  const soleImage = layer.animationFrames?.[0] || layer.imageData;
  await drawCursorWithCosmeticAsync(ctx, cx, cy, r, mount.getCursorColor(), soleImage, {
    cosmeticOnly: true,
    animationFrames: null,
  });
  toBlackSilhouette(canvas);
}

function createIndexCard(item: ShopCosmeticDto, owned: boolean): HTMLElement {
  const card = document.createElement("article");
  card.className = "inventoryCosmeticCard inventoryIndexCard";
  card.classList.toggle("inventoryIndexCard--owned", owned);
  card.classList.toggle("inventoryIndexCard--locked", !owned);
  card.setAttribute("aria-label", owned ? item.name : `${item.name} (not obtained)`);

  const isTrail = itemCategory(item) === "trail";
  const isBanner = itemCategory(item) === "banner";
  const trailVis: TrailCosmeticVisual | null = isTrail
    ? {
        trailMode: item.trailMode === "particle" ? "particle" : "multiColor",
        trailColors: item.trailColors ?? null,
        imageData: item.imageData,
        animationFrames: item.animationFrames ?? null,
      }
    : null;
  if (trailVis) {
    const badge = document.createElement("span");
    badge.className = "shopCosmeticTrailBadge";
    badge.textContent = trailModeBadgeLabel(trailVis.trailMode);
    card.appendChild(badge);
  }

  const previewWrap = document.createElement("div");
  previewWrap.className = isBanner
    ? "shopCosmeticCardPreviewWrap shopCosmeticCardPreviewWrap--banner"
    : "shopCosmeticCardPreviewWrap";

  const idleCanvas = document.createElement("canvas");
  idleCanvas.className = isBanner
    ? "shopCosmeticCardPreview shopCosmeticCardPreview--idle shopCosmeticCardPreview--banner"
    : "shopCosmeticCardPreview shopCosmeticCardPreview--idle";
  idleCanvas.width = isBanner ? BANNER_CARD_W : INV_CARD_PREVIEW_SIZE;
  idleCanvas.height = isBanner ? BANNER_CARD_H : INV_CARD_PREVIEW_SIZE;

  const hoverCanvas = document.createElement("canvas");
  hoverCanvas.className = isBanner
    ? "shopCosmeticCardPreview shopCosmeticCardPreview--hover shopCosmeticCardPreview--banner"
    : "shopCosmeticCardPreview shopCosmeticCardPreview--hover";
  hoverCanvas.width = isBanner ? BANNER_CARD_W : INV_CARD_PREVIEW_SIZE;
  hoverCanvas.height = isBanner ? BANNER_CARD_H : INV_CARD_PREVIEW_SIZE;

  previewWrap.appendChild(idleCanvas);
  previewWrap.appendChild(hoverCanvas);
  card.appendChild(previewWrap);

  const name = document.createElement("h3");
  name.className = "inventoryCosmeticCardName";
  name.textContent = item.name;
  card.appendChild(name);

  const acquire = document.createElement("p");
  acquire.className = "inventoryIndexAcquire";
  const lines = buildAcquisitionLines(item, crateSourceByCosmeticId.get(item.id));
  acquire.textContent = lines.join(" · ");
  card.appendChild(acquire);

  const bindPreviewHold = (start: () => void, stop: () => void) => {
    card.addEventListener("mouseenter", start);
    card.addEventListener("mouseleave", stop);
    previewWrap.addEventListener(
      "touchstart",
      () => {
        card.classList.add("inventoryCosmeticCard--preview");
        start();
      },
      { passive: true },
    );
    previewWrap.addEventListener(
      "touchend",
      () => {
        card.classList.remove("inventoryCosmeticCard--preview");
        stop();
      },
      { passive: true },
    );
    previewWrap.addEventListener(
      "touchcancel",
      () => {
        card.classList.remove("inventoryCosmeticCard--preview");
        stop();
      },
      { passive: true },
    );
  };

  if (trailVis) {
    void (async () => {
      const layer = await resolveDisplay(item);
      trailVis.imageData = layer.imageData;
      trailVis.animationFrames = layer.animationFrames;
      if (owned) {
        await paintTrailCardIdle(idleCanvas, trailVis);
        let previewState = createTrailPreviewState();
        let t0 = performance.now();
        const paintHover = async () => {
          const tSec = (performance.now() - t0) / 1000;
          previewState = await paintTrailCardPreview(
            hoverCanvas,
            trailVis,
            tSec,
            "#ffffff",
            previewState,
          );
        };
        void paintHover();
        bindPreviewHold(
          () => {
            previewState = createTrailPreviewState();
            t0 = performance.now();
            startCosmeticPreviewLoop(
              hoverCanvas,
              () => paintHover(),
              () =>
                card.isConnected &&
                (card.matches(":hover") || card.classList.contains("inventoryCosmeticCard--preview")),
            );
          },
          () => stopCosmeticPreviewLoop(hoverCanvas),
        );
      } else {
        await paintIndexSilhouette(idleCanvas, item, layer);
        let previewState = createTrailPreviewState();
        let t0 = performance.now();
        const paintHover = async () => {
          const tSec = (performance.now() - t0) / 1000;
          previewState = await paintTrailCardPreview(
            hoverCanvas,
            trailVis,
            tSec,
            "#ffffff",
            previewState,
          );
        };
        void paintHover();
        bindPreviewHold(
          () => {
            previewState = createTrailPreviewState();
            t0 = performance.now();
            startCosmeticPreviewLoop(
              hoverCanvas,
              () => paintHover(),
              () =>
                card.isConnected &&
                (card.matches(":hover") || card.classList.contains("inventoryCosmeticCard--preview")),
            );
          },
          () => stopCosmeticPreviewLoop(hoverCanvas),
        );
      }
    })();
    return card;
  }

  if (isBanner) {
    void resolveDisplay(item).then(async (layer) => {
      await ensureBannerCardImages(layer.imageData, layer.animationFrames);
      if (owned) {
        paintBannerCardArt(idleCanvas, layer.imageData, layer.animationFrames, "idle");
      } else {
        await paintIndexSilhouette(idleCanvas, item, layer);
      }
      paintBannerCardArt(hoverCanvas, layer.imageData, layer.animationFrames, "preview");
      const hasAnim = !!(layer.animationFrames && layer.animationFrames.length > 0);
      bindPreviewHold(
        () => {
          if (!hasAnim) return;
          startCosmeticPreviewLoop(
            hoverCanvas,
            () => paintBannerCardArt(hoverCanvas, layer.imageData, layer.animationFrames, "preview"),
            () =>
              card.isConnected &&
              (card.matches(":hover") || card.classList.contains("inventoryCosmeticCard--preview")),
          );
        },
        () => stopCosmeticPreviewLoop(hoverCanvas),
      );
    });
    return card;
  }

  void resolveDisplay(item).then(async (layer) => {
    await loadCosmeticImage(layer.imageData);
    if (layer.animationFrames?.length) {
      await Promise.all(layer.animationFrames.map((f) => loadCosmeticImage(f)));
    }
    const idleFrames = layer.animationFrames?.length ? [layer.animationFrames[0]] : null;
    const hideBase = !!item.newCursor;
    if (owned) {
      paintCardLayer(idleCanvas, layer.imageData, "idle", idleFrames, { hideBaseCursor: hideBase });
    } else {
      await paintIndexSilhouette(idleCanvas, item, layer);
    }
    paintCardLayer(hoverCanvas, layer.imageData, "preview", layer.animationFrames, {
      hideBaseCursor: hideBase,
    });
    const hasAnim = !!(layer.animationFrames && layer.animationFrames.length > 0);
    bindPreviewHold(
      () => {
        if (!hasAnim) return;
        startCosmeticPreviewLoop(
          hoverCanvas,
          () => {
            paintCardLayer(hoverCanvas, layer.imageData, "preview", layer.animationFrames, {
              hideBaseCursor: hideBase,
            });
          },
          () =>
            card.isConnected &&
            (card.matches(":hover") || card.classList.contains("inventoryCosmeticCard--preview")),
        );
      },
      () => stopCosmeticPreviewLoop(hoverCanvas),
    );
  });

  return card;
}

function renderIndexPanel() {
  if (!mount) return;
  const panel = mount.indexPanelEl;
  const select = mount.indexFilterEl;
  const optionValues = INDEX_FILTER_OPTIONS.map((o) => o.value).join(",");
  if (select.dataset.indexFilterOpts !== optionValues) {
    select.replaceChildren();
    for (const opt of INDEX_FILTER_OPTIONS) {
      const o = document.createElement("option");
      o.value = opt.value;
      o.textContent = opt.label;
      select.appendChild(o);
    }
    select.dataset.indexFilterOpts = optionValues;
  }
  if (!INDEX_FILTER_OPTIONS.some((o) => o.value === indexFilter)) indexFilter = "all";
  select.value = indexFilter;
  select.onchange = () => {
    indexFilter = select.value as IndexFilter;
    renderIndexPanel();
  };

  let gridHost = panel.querySelector<HTMLElement>("#inventoryIndexGrid");
  if (!gridHost) {
    gridHost = document.createElement("div");
    gridHost.id = "inventoryIndexGrid";
    panel.appendChild(gridHost);
  }
  gridHost.replaceChildren();

  const ownedIds = new Set(getDotsState().ownedCosmeticIds);
  if (!catalogItems.length) {
    const empty = document.createElement("p");
    empty.className = "accessHint";
    empty.textContent = "No cosmetics in the catalog yet.";
    gridHost.appendChild(empty);
    return;
  }

  const filtered = filterIndexItems(catalogItems, indexFilter, ownedIds);
  if (!filtered.length) {
    const empty = document.createElement("p");
    empty.className = "accessHint";
    empty.textContent =
      indexFilter === "obtained"
        ? "No obtained cosmetics yet."
        : indexFilter === "not_obtained"
          ? "You’ve collected everything in the Index."
          : "No cosmetics in the catalog yet.";
    gridHost.appendChild(empty);
    return;
  }

  const groups = groupIndexByCategory(filtered, itemCategory, CATEGORY_ORDER);
  for (const { category, items } of groups) {
    const section = document.createElement("section");
    section.className = "shopCategorySection";
    const title = document.createElement("h2");
    title.className = "shopCategoryTitle";
    title.textContent = CATEGORY_LABELS[category];
    section.appendChild(title);
    const grid = document.createElement("div");
    grid.className = "shopCosmeticsGrid shopCosmeticsGrid--section";
    for (const item of items) {
      grid.appendChild(createIndexCard(item, ownedIds.has(item.id)));
    }
    section.appendChild(grid);
    gridHost.appendChild(section);
  }
}

function setInventoryView(view: "cosmetics" | "outfits" | "index") {
  if (!mount) return;
  inventoryView = view;
  const cosmeticsOn = view === "cosmetics";
  const outfitsOn = view === "outfits";
  const indexOn = view === "index";
  mount.gridEl.classList.toggle("hidden", !cosmeticsOn);
  mount.outfitsPanelEl.classList.toggle("hidden", !outfitsOn);
  mount.indexPanelEl.classList.toggle("hidden", !indexOn);
  mount.tabCosmeticsBtn.classList.toggle("green", cosmeticsOn);
  mount.tabOutfitsBtn.classList.toggle("green", outfitsOn);
  mount.tabIndexBtn.classList.toggle("green", indexOn);
  mount.tabCosmeticsBtn.setAttribute("aria-selected", cosmeticsOn ? "true" : "false");
  mount.tabOutfitsBtn.setAttribute("aria-selected", outfitsOn ? "true" : "false");
  mount.tabIndexBtn.setAttribute("aria-selected", indexOn ? "true" : "false");
  renderActiveView();
}

async function unequipItem(item: ShopCosmeticDto, btn?: HTMLButtonElement): Promise<boolean> {
  if (!mount) return false;
  if (!mount.isSignedIn()) {
    mount.statusEl.textContent = "Sign in to unequip cosmetics.";
    return false;
  }
  if (btn) btn.disabled = true;
  try {
    const res = await shopEquipCosmetic(item.id, "unequip");
    if (res.dots) applyDotsFromApi(res.dots);
    else {
      if (res.equippedCosmeticIds) setEquippedCosmeticIds(res.equippedCosmeticIds);
      if (res.equippedThemeChannels) setEquippedThemeChannels(res.equippedThemeChannels);
      else if (!res.equippedCosmeticIds) {
        setEquippedCosmeticIds(getDotsState().equippedCosmeticIds.filter((id) => id !== item.id));
      }
    }
    applyEquippedThemeCosmetics();
    refreshGameThemePresentation();
    mount.onEquippedChanged();
    renderActiveView();
    mount.statusEl.textContent = "Unequipped.";
    return true;
  } catch (e) {
    mount.statusEl.textContent = e instanceof Error ? e.message : "Unequip failed.";
    if (btn) btn.disabled = false;
    return false;
  }
}

/** UI list is top-layer-first; draw/storage order is bottom→top. Cursor stays last in UI. */
function uiOrderFromDrawOrder(drawIds: string[], isCursor: (id: string) => boolean): string[] {
  const reversed = [...drawIds].reverse();
  const cursorId = reversed.find((id) => isCursor(id));
  if (!cursorId) return reversed;
  return [...reversed.filter((id) => id !== cursorId), cursorId];
}

function drawOrderFromUiOrder(uiIds: string[], isCursor: (id: string) => boolean): string[] {
  const cursorId = uiIds.find((id) => isCursor(id));
  const rest = uiIds.filter((id) => id !== cursorId);
  const drawRest = [...rest].reverse();
  return cursorId ? [cursorId, ...drawRest] : drawRest;
}

async function commitEquippedDrawOrder(drawIds: string[]) {
  if (!mount) return;
  setEquippedCosmeticIds(drawIds);
  mount.onEquippedChanged();
  void paintLivePreview();
  if (!mount.isSignedIn()) {
    renderEquippedList();
    return;
  }
  try {
    const res = await shopSetEquippedCosmetics(drawIds);
    if (res.dots) applyDotsFromApi(res.dots);
    else if (res.equippedCosmeticIds) setEquippedCosmeticIds(res.equippedCosmeticIds);
    mount.onEquippedChanged();
    renderEquippedList();
    void paintLivePreview();
  } catch (e) {
    mount.statusEl.textContent = e instanceof Error ? e.message : "Could not reorder.";
    renderEquippedList();
    void paintLivePreview();
  }
}

function bindEquippedRowDrag(row: HTMLElement, handle: HTMLElement, rowsHost: HTMLElement) {
  let dragging = false;
  let pointerId: number | null = null;

  const onPointerMove = (ev: PointerEvent) => {
    if (!dragging || pointerId !== ev.pointerId) return;
    const movable = [
      ...rowsHost.querySelectorAll<HTMLElement>(
        ".inventoryEquippedRow:not(.inventoryEquippedRow--locked)",
      ),
    ];
    const locked = rowsHost.querySelector<HTMLElement>(".inventoryEquippedRow--locked");
    const y = ev.clientY;
    let insertBefore: HTMLElement | null = null;
    for (const other of movable) {
      if (other === row) continue;
      const rect = other.getBoundingClientRect();
      if (y < rect.top + rect.height / 2) {
        insertBefore = other;
        break;
      }
    }
    if (insertBefore) rowsHost.insertBefore(row, insertBefore);
    else if (locked) rowsHost.insertBefore(row, locked);
    else rowsHost.appendChild(row);
  };

  const endDrag = (ev: PointerEvent) => {
    if (!dragging || pointerId !== ev.pointerId) return;
    dragging = false;
    pointerId = null;
    row.classList.remove("inventoryEquippedRow--dragging");
    try {
      handle.releasePointerCapture(ev.pointerId);
    } catch {
      /* already released */
    }
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", endDrag);
    window.removeEventListener("pointercancel", endDrag);

    const byId = new Map(ownedItems.map((item) => [item.id, item]));
    const isCursor = (id: string) => {
      const item = byId.get(id);
      return item ? itemCategory(item) === "cursor_color" : false;
    };
    const uiIds = [...rowsHost.querySelectorAll<HTMLElement>(".inventoryEquippedRow")]
      .map((el) => el.dataset.cosmeticId)
      .filter((id): id is string => !!id);
    const drawIds = drawOrderFromUiOrder(uiIds, isCursor);
    const prev = getDotsState().equippedCosmeticIds;
    if (drawIds.length === prev.length && drawIds.every((id, i) => id === prev[i])) return;
    void commitEquippedDrawOrder(drawIds);
  };

  handle.addEventListener("pointerdown", (ev) => {
    if (ev.button != null && ev.button !== 0) return;
    ev.preventDefault();
    dragging = true;
    pointerId = ev.pointerId;
    row.classList.add("inventoryEquippedRow--dragging");
    handle.setPointerCapture(ev.pointerId);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
  });
}

function renderEquippedList() {
  if (!mount?.equippedListEl) return;
  const list = mount.equippedListEl;
  list.replaceChildren();

  const title = document.createElement("p");
  title.className = "inventoryEquippedListTitle";
  title.textContent = "Equipped";
  list.appendChild(title);

  const ids = getDotsState().equippedCosmeticIds;
  if (!ids.length) {
    const empty = document.createElement("p");
    empty.className = "inventoryEquippedEmpty";
    empty.textContent = "Nothing equipped.";
    list.appendChild(empty);
    return;
  }

  const rowsHost = document.createElement("div");
  rowsHost.className = "inventoryEquippedRows";
  list.appendChild(rowsHost);

  const byId = new Map(ownedItems.map((item) => [item.id, item]));
  const isCursor = (id: string) => {
    const item = byId.get(id);
    return item ? itemCategory(item) === "cursor_color" : false;
  };
  // Top of the list = topmost draw layer; Cursor (min layer) stays at the bottom, locked.
  for (const id of uiOrderFromDrawOrder(ids, isCursor)) {
    const item = byId.get(id);
    const locked = isCursor(id);
    const row = document.createElement("div");
    row.className = "inventoryEquippedRow";
    if (locked) row.classList.add("inventoryEquippedRow--locked");
    row.dataset.cosmeticId = id;

    if (!locked) {
      const handle = document.createElement("button");
      handle.type = "button";
      handle.className = "inventoryEquippedGrip";
      handle.setAttribute("aria-label", `Reorder ${item?.name || id}`);
      handle.title = "Drag to change layer";
      handle.innerHTML = EQUIPPED_GRIP_ICON;
      bindEquippedRowDrag(row, handle, rowsHost);
      row.appendChild(handle);
    } else {
      const spacer = document.createElement("span");
      spacer.className = "inventoryEquippedGripSpacer";
      spacer.setAttribute("aria-hidden", "true");
      row.appendChild(spacer);
    }

    const meta = document.createElement("div");
    meta.className = "inventoryEquippedMeta";
    const cat = document.createElement("span");
    cat.className = "inventoryEquippedCat";
    cat.textContent = item ? CATEGORY_LABELS[itemCategory(item)] : "Cosmetic";
    const name = document.createElement("span");
    name.className = "inventoryEquippedName";
    name.textContent = item?.name || id;
    meta.appendChild(cat);
    meta.appendChild(name);

    const unequipBtn = document.createElement("button");
    unequipBtn.type = "button";
    unequipBtn.className = "inventoryUnequipBtn inventoryEquippedUnequipBtn";
    unequipBtn.setAttribute("aria-label", `Unequip ${item?.name || id}`);
    unequipBtn.title = "Unequip";
    unequipBtn.innerHTML = UNEQUIP_ICON;
    unequipBtn.disabled = !item || !mount.isSignedIn();
    unequipBtn.onclick = () => {
      if (!item) return;
      void unequipItem(item, unequipBtn);
    };

    row.appendChild(meta);
    row.appendChild(unequipBtn);
    if (item) {
      row.title = "Drag out to unequip · grip reorders layers";
      bindUnequipDragFromElement(row, row, () => item, () => makeItemGhostCanvas(item));
    }
    rowsHost.appendChild(row);
  }
}

function renderActiveView() {
  if (!mount) return;
  if (inventoryView === "outfits") renderOutfitsPanel();
  else if (inventoryView === "index") renderIndexPanel();
  else renderGrid();
  renderEquippedList();
  void paintLivePreview();
}

function renderGrid() {
  if (!mount) return;
  mount.gridEl.replaceChildren();
  const equipped = new Set(getDotsState().equippedCosmeticIds);

  if (!ownedItems.length) {
    const empty = document.createElement("p");
    empty.className = "accessHint";
    empty.textContent = mount.isSignedIn()
      ? "No cosmetics yet — visit the Shop to buy or claim some."
      : "Sign in to view your inventory.";
    mount.gridEl.appendChild(empty);
    return;
  }

  for (const cat of CATEGORY_ORDER) {
    const items = ownedItems.filter((i) => itemCategory(i) === cat);
    if (!items.length) continue;
    const section = document.createElement("section");
    section.className = "shopCategorySection";
    const title = document.createElement("h2");
    title.className = "shopCategoryTitle";
    title.textContent = CATEGORY_LABELS[cat];
    section.appendChild(title);
    const grid = document.createElement("div");
    grid.className = "shopCosmeticsGrid shopCosmeticsGrid--section";
    for (const item of items) grid.appendChild(createCard(item, equipped));
    section.appendChild(grid);
    mount.gridEl.appendChild(section);
  }
}

async function paintOutfitPreview(
  canvas: HTMLCanvasElement,
  ids: string[],
  serialHolder: { n: number },
  cursorColor?: string,
) {
  const serial = ++serialHolder.n;
  const ctx = canvas.getContext("2d");
  if (!ctx || !mount) return;
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  const cx = w * 0.5;
  const cy = h * 0.5;
  const fill = Math.min(w, h) * 0.78;
  const r = fill / (COSMETIC_ART_SIZE / COSMETIC_REF_CURSOR_R);
  ctx.save();
  ctx.fillStyle = cursorColor || mount.getCursorColor();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  const byId = new Map(ownedItems.map((item) => [item.id, item]));
  for (const id of ids) {
    if (serial !== serialHolder.n) return;
    const item = byId.get(id);
    if (!item) continue;
    if (itemCategory(item) === "trail" || itemCategory(item) === "banner" || itemCategory(item) === "theme") continue;
    const layer = await resolveDisplay(item);
    if (serial !== serialHolder.n) return;
    await loadCosmeticImage(layer.imageData);
    if (layer.animationFrames?.length) {
      await Promise.all(layer.animationFrames.map((f) => loadCosmeticImage(f)));
    }
    if (serial !== serialHolder.n) return;
    drawCosmeticOverlay(ctx, cx, cy, r, getCachedCosmeticImage(layer.imageData));
    if (layer.animationFrames?.length) {
      drawCosmeticOverlay(ctx, cx, cy, r, getCachedCosmeticImage(layer.animationFrames[0]));
    }
  }
}

function createOutfitCard(outfit: CosmeticOutfitDto): HTMLElement {
  const card = document.createElement("article");
  card.className = "inventoryOutfitCard";

  const name = document.createElement("h3");
  name.className = "inventoryOutfitCardName";
  if (outfit.name) name.textContent = outfit.name;
  else {
    name.textContent = "Unnamed";
    name.classList.add("inventoryOutfitCardName--empty");
  }
  card.appendChild(name);

  const canvas = document.createElement("canvas");
  canvas.className = "inventoryOutfitCardPreview";
  canvas.width = OUTFIT_PREVIEW_SIZE;
  canvas.height = OUTFIT_PREVIEW_SIZE;
  card.appendChild(canvas);
  const serialHolder = { n: 0 };
  void paintOutfitPreview(canvas, outfit.equippedCosmeticIds, serialHolder, outfit.cursorColor);

  const btns = document.createElement("div");
  btns.className = "inventoryOutfitCardBtns";

  const equipBtn = document.createElement("button");
  equipBtn.type = "button";
  equipBtn.className = "btn green";
  equipBtn.textContent = "Equip";
  equipBtn.onclick = () => void equipOutfit(outfit, equipBtn);

  const trashBtn = document.createElement("button");
  trashBtn.type = "button";
  trashBtn.className = "inventoryOutfitTrashBtn";
  trashBtn.title = "Delete outfit";
  trashBtn.setAttribute("aria-label", "Delete outfit");
  trashBtn.innerHTML = TRASH_ICON;
  trashBtn.onclick = () => void deleteOutfit(outfit, trashBtn);

  btns.appendChild(equipBtn);
  btns.appendChild(trashBtn);
  card.appendChild(btns);
  return card;
}

async function addOutfit() {
  if (!mount) return;
  if (!mount.isSignedIn()) {
    mount.statusEl.textContent = "Sign in to save outfits.";
    return;
  }
  const ids = getDotsState().equippedCosmeticIds;
  if (!ids.length) {
    mount.statusEl.textContent = "Equip at least one cosmetic before saving an outfit.";
    return;
  }
  const raw = prompt("Outfit name (optional):");
  if (raw === null) return;
  const name = raw.trim();
  try {
    mount.statusEl.textContent = "Saving outfit…";
    const res = await shopSaveOutfit({
      name,
      equippedCosmeticIds: ids,
      cursorColor: mount.getCursorColor(),
      trailColor: mount.getTrailColor(),
    });
    if (res.dots) applyDotsFromApi(res.dots);
    else if (res.outfits) setOutfits(res.outfits);
    renderOutfitsPanel();
    mount.statusEl.textContent = name ? `Saved outfit “${name}”.` : "Saved outfit.";
  } catch (e) {
    const code = e instanceof Error ? e.message : "save_failed";
    if (code === "outfit_empty") mount.statusEl.textContent = "Outfit is empty.";
    else if (code === "outfits_full") mount.statusEl.textContent = "Outfit limit reached.";
    else mount.statusEl.textContent = "Could not save outfit.";
  }
}

async function equipOutfit(outfit: CosmeticOutfitDto, btn: HTMLButtonElement) {
  if (!mount) return;
  if (!mount.isSignedIn()) {
    mount.statusEl.textContent = "Sign in to equip outfits.";
    return;
  }
  btn.disabled = true;
  try {
    const res = await shopSetEquippedCosmetics(outfit.equippedCosmeticIds);
    if (res.dots) applyDotsFromApi(res.dots);
    else if (res.equippedCosmeticIds) setEquippedCosmeticIds(res.equippedCosmeticIds);
    else setEquippedCosmeticIds(outfit.equippedCosmeticIds);
    if (res.outfits) setOutfits(res.outfits);
    if (outfit.cursorColor || outfit.trailColor) {
      mount.applyOutfitColors({
        cursorColor: outfit.cursorColor,
        trailColor: outfit.trailColor,
      });
    }
    mount.onEquippedChanged();
    void paintLivePreview();
    mount.statusEl.textContent = outfit.name
      ? `Equipped outfit “${outfit.name}”.`
      : "Equipped outfit.";
  } catch (e) {
    mount.statusEl.textContent = e instanceof Error ? e.message : "Could not equip outfit.";
  } finally {
    btn.disabled = false;
  }
}

async function deleteOutfit(outfit: CosmeticOutfitDto, btn: HTMLButtonElement) {
  if (!mount) return;
  if (!mount.isSignedIn()) return;
  const label = outfit.name || "this outfit";
  if (!confirm(`Delete ${label}?`)) return;
  btn.disabled = true;
  try {
    const res = await shopDeleteOutfit(outfit.id);
    if (res.dots) applyDotsFromApi(res.dots);
    else if (res.outfits) setOutfits(res.outfits);
    renderOutfitsPanel();
    mount.statusEl.textContent = "Outfit deleted.";
  } catch (e) {
    mount.statusEl.textContent = e instanceof Error ? e.message : "Could not delete outfit.";
    btn.disabled = false;
  }
}

function renderOutfitsPanel() {
  if (!mount) return;
  mount.outfitsPanelEl.replaceChildren();

  const actions = document.createElement("div");
  actions.className = "inventoryOutfitsActions";
  const addBtn = document.createElement("button");
  addBtn.type = "button";
  addBtn.className = "btn green";
  addBtn.textContent = "Add Outfit";
  addBtn.onclick = () => void addOutfit();
  actions.appendChild(addBtn);
  mount.outfitsPanelEl.appendChild(actions);

  const outfits = getDotsState().outfits;
  if (!outfits.length) {
    const empty = document.createElement("p");
    empty.className = "accessHint";
    empty.textContent = mount.isSignedIn()
      ? "No outfits yet — equip cosmetics, then Add Outfit."
      : "Sign in to save and load outfits.";
    mount.outfitsPanelEl.appendChild(empty);
    return;
  }

  const grid = document.createElement("div");
  grid.className = "inventoryOutfitsGrid";
  for (const outfit of outfits) grid.appendChild(createOutfitCard(outfit));
  mount.outfitsPanelEl.appendChild(grid);
}

export async function openInventoryScreen(m: InventoryMount) {
  mount = m;
  displayById.clear();
  livePreviewSerial = 0;
  inventoryView = "cosmetics";
  m.statusEl.textContent = "Loading…";
  m.screen.classList.remove("hidden");
  m.balanceEl.textContent = formatDots(getDotsBalance());
  m.tabCosmeticsBtn.onclick = () => setInventoryView("cosmetics");
  m.tabOutfitsBtn.onclick = () => setInventoryView("outfits");
  m.tabIndexBtn.onclick = () => setInventoryView("index");
  setInventoryView("cosmetics");
  void paintLivePreview();
  bindPreviewUnequipDrags();
  try {
    const data = await fetchShopCatalog();
    if (data.dots) applyDotsFromApi(data.dots);
    m.balanceEl.textContent = formatDots(getDotsBalance());
    const owned = new Set(getDotsState().ownedCosmeticIds);
    crateSourceByCosmeticId = buildCrateSourceMap(data.crates);
    catalogItems = filterIndexCatalog(data.items, crateSourceByCosmeticId, owned);
    ownedItems = data.items.filter((i) => owned.has(i.id));
    registerThemeCosmeticBatch(data.items);
    void preloadCosmeticImages(
      [...ownedItems, ...catalogItems].flatMap((c) => [
        c.imageData,
        c.outlineMaskData,
        ...(c.animationFrames ?? []),
        c.matchCursorMaskData,
        ...(c.matchCursorMaskFrames ?? []),
      ]),
    );
    renderActiveView();
    m.statusEl.textContent = m.isSignedIn() ? "" : "Guest mode — sign in to sync inventory.";
    m.onEquippedChanged();
  } catch (e) {
    m.statusEl.textContent = e instanceof Error ? e.message : "Could not load Inventory.";
    void paintLivePreview();
  }
}

export function closeInventoryScreen() {
  if (livePreviewResolveTimer != null) {
    clearTimeout(livePreviewResolveTimer);
    livePreviewResolveTimer = null;
  }
  if (mount?.livePreviewCanvas) stopCosmeticPreviewLoop(mount.livePreviewCanvas);
  if (mount?.bannerPreviewCanvas) stopCosmeticPreviewLoop(mount.bannerPreviewCanvas);
  if (mount) mount.screen.classList.add("hidden");
  mount = null;
  ownedItems = [];
  catalogItems = [];
  crateSourceByCosmeticId = new Map();
  livePreviewLayers = [];
  displayById.clear();
  livePreviewSerial = 0;
  inventoryView = "cosmetics";
  indexFilter = "all";
  previewUnequipBound = false;
  for (const unbind of previewUnequipUnbinds) unbind();
  previewUnequipUnbinds = [];
}

/** Update live preview immediately; debounce match-cursor layer re-resolve while dragging RGB. */
export function refreshInventoryCursorColor() {
  if (!mount) return;
  displayById.clear();
  drawLivePreviewFrame();
  ensureLivePreviewLoop();
  if (livePreviewResolveTimer != null) clearTimeout(livePreviewResolveTimer);
  livePreviewResolveTimer = setTimeout(() => {
    livePreviewResolveTimer = null;
    void paintLivePreview();
  }, 60);
}
